import { useMemo, useState } from 'react';
import { Plus, X, Loader2, Dice5 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useCreateEvent } from '@/hooks/useEvents';

/**
 * SlotForm — Formulario admin para crear una Tragamonedas (type='slot').
 * Clona la UI de fedesuete (fede-slots.png):
 *   - Nombre
 *   - Símbolos del carrete (mín 3): emoji + nombre + eliminar, "+ Símbolo"
 *   - Premios (probabilidad total = 100%): emoji + label + tipo premio
 *     (Fichas / Bono 100% / Bono 200%) + monto $ + % + eliminar + Combo (3 selects)
 *   - Depósito mínimo + Duración
 *
 * Arma `config` (symbols[] + reels:3 + paytable[]) y llama eventsApi.create
 * vía el hook useCreateEvent (mismo patrón que eventos.tsx).
 */

type PrizeKind = 'fichas' | 'bono100' | 'bono200' | 'free_spin' | 'none';

interface SymbolRow {
  id: string;
  emoji: string;
  name: string;
}

interface PrizeRow {
  id: string;
  emoji: string;
  label: string;
  prizeKind: PrizeKind;
  amount: string;
  weight: string;
  combo: [string, string, string];
}

const REELS = 3;

const PRIZE_KIND_OPTIONS: { value: PrizeKind; label: string }[] = [
  { value: 'fichas', label: 'Fichas' },
  { value: 'bono100', label: 'Bono 100%' },
  { value: 'bono200', label: 'Bono 200%' },
  { value: 'free_spin', label: 'Tirada gratis' },
];

let uid = 0;
const nextId = () => `r${++uid}_${Date.now().toString(36)}`;

/** Defaults del screenshot de fedesuete. */
const DEFAULT_SYMBOLS: SymbolRow[] = [
  { id: nextId(), emoji: '🍒', name: 'Cereza' },
  { id: nextId(), emoji: '🍋', name: 'Limón' },
  { id: nextId(), emoji: '🍊', name: 'Naranja' },
  { id: nextId(), emoji: '⭐', name: 'Estrella' },
  { id: nextId(), emoji: '💎', name: 'Diamante' },
  { id: nextId(), emoji: '7️⃣', name: 'Siete' },
];

const DEFAULT_PRIZES: PrizeRow[] = [
  { id: nextId(), emoji: '💎', label: 'JACKPOT', prizeKind: 'fichas', amount: '50000', weight: '2', combo: ['7️⃣', '7️⃣', '7️⃣'] },
  { id: nextId(), emoji: '🔥', label: '10.000 Fichas', prizeKind: 'fichas', amount: '10000', weight: '8', combo: ['💎', '💎', '💎'] },
  { id: nextId(), emoji: '⭐', label: '5.000 Fichas', prizeKind: 'fichas', amount: '5000', weight: '15', combo: ['⭐', '⭐', '⭐'] },
  { id: nextId(), emoji: '🍀', label: '2.000 Fichas', prizeKind: 'fichas', amount: '2000', weight: '20', combo: ['🍊', '🍊', '🍊'] },
  { id: nextId(), emoji: '🎟️', label: 'Bono 200%', prizeKind: 'bono200', amount: '0', weight: '5', combo: ['🍒', '🍒', '🍒'] },
];

export interface SlotFormProps {
  /** Callback al crear con éxito (p. ej. cerrar el dialog / cambiar de tab). */
  onCreated?: () => void;
}

export function SlotForm({ onCreated }: SlotFormProps) {
  const createMutation = useCreateEvent();

  const [name, setName] = useState('');
  const [depositMin, setDepositMin] = useState('');
  const [durationMin, setDurationMin] = useState('');
  const [wagerMultiplier, setWagerMultiplier] = useState('1');
  const [maxPlays, setMaxPlays] = useState('1');
  const [symbols, setSymbols] = useState<SymbolRow[]>(DEFAULT_SYMBOLS);
  const [prizes, setPrizes] = useState<PrizeRow[]>(DEFAULT_PRIZES);
  const [formError, setFormError] = useState<string | null>(null);

  // Opciones de combo = símbolos con emoji cargado.
  const symbolOptions = useMemo(
    () => symbols.filter((s) => s.emoji.trim().length > 0),
    [symbols],
  );

  // Suma de probabilidades (debe ser < 100; el resto es "sin premio").
  const totalWeight = useMemo(
    () => prizes.reduce((acc, p) => acc + (Number(p.weight) || 0), 0),
    [prizes],
  );

  // RTP esperado en fichas = Σ(amount_i * prob_i). Sólo cuenta premios `fichas`
  // (los bonos no se devuelven como saldo directo). Sirve para que el admin no
  // configure sin querer un payout esperado descontrolado.
  const expectedFichasPayout = useMemo(
    () =>
      prizes.reduce((acc, p) => {
        if (p.prizeKind !== 'fichas') return acc;
        const amount = Number(p.amount) || 0;
        const prob = (Number(p.weight) || 0) / 100;
        return acc + amount * prob;
      }, 0),
    [prizes],
  );

  // ---- símbolos ----
  const addSymbol = () =>
    setSymbols((prev) => [...prev, { id: nextId(), emoji: '', name: '' }]);

  const updateSymbol = (id: string, patch: Partial<SymbolRow>) =>
    setSymbols((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const removeSymbol = (id: string) =>
    setSymbols((prev) => (prev.length <= 3 ? prev : prev.filter((s) => s.id !== id)));

  // ---- premios ----
  const addPrize = () =>
    setPrizes((prev) => [
      ...prev,
      {
        id: nextId(),
        emoji: symbolOptions[0]?.emoji ?? '',
        label: '',
        prizeKind: 'fichas',
        amount: '',
        weight: '',
        combo: [
          symbolOptions[0]?.emoji ?? '',
          symbolOptions[0]?.emoji ?? '',
          symbolOptions[0]?.emoji ?? '',
        ],
      },
    ]);

  const updatePrize = (id: string, patch: Partial<PrizeRow>) =>
    setPrizes((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));

  const updatePrizeCombo = (id: string, idx: number, emoji: string) =>
    setPrizes((prev) =>
      prev.map((p) => {
        if (p.id !== id) return p;
        const combo = [...p.combo] as [string, string, string];
        combo[idx] = emoji;
        return { ...p, combo };
      }),
    );

  const removePrize = (id: string) =>
    setPrizes((prev) => prev.filter((p) => p.id !== id));

  const buildConfig = () => ({
    reels: REELS,
    wagerMultiplier: Number(wagerMultiplier) || 1,
    // Tiradas por persona. Default 1: hasta el 2026-07-29 no había tope y una
    // tragamonedas que acredita fichas reales se farmea sola. 0 = ilimitado.
    maxPlays: Math.max(0, Math.floor(Number(maxPlays) || 0)),
    symbols: symbolOptions.map((s) => ({
      emoji: s.emoji.trim(),
      name: s.name.trim() || undefined,
      // Peso de aparición uniforme por defecto; el balance fino lo da la paytable.
      weight: 1,
    })),
    paytable: prizes.map((p) => ({
      combo: p.combo,
      label: p.label.trim() || undefined,
      prizeKind: p.prizeKind,
      amount: Number(p.amount) || 0,
      weight: Number(p.weight) || 0,
    })),
  });

  const validate = (): string | null => {
    if (!name.trim()) return 'El nombre es requerido.';
    if (symbolOptions.length < 3) return 'Cargá al menos 3 símbolos con emoji.';
    if (prizes.length === 0) return 'Agregá al menos un premio.';
    const validEmojis = new Set(symbolOptions.map((s) => s.emoji.trim()));
    for (const p of prizes) {
      if (p.combo.some((c) => !validEmojis.has(c))) {
        return `El combo del premio "${p.label || '(sin nombre)'}" usa un símbolo inexistente.`;
      }
    }
    if (totalWeight > 100) {
      return `La probabilidad total (${totalWeight}%) supera 100%.`;
    }
    // Anti-RTP>100: si la suma llega o supera 100% no queda margen de derrota
    // (loseWeight=0 en el resolver) y la tragamonedas SIEMPRE paga. Exigimos
    // dejar un margen (<100) para que la casa pueda no premiar.
    if (totalWeight >= 100) {
      return `La probabilidad total debe ser MENOR a 100% (hay ${totalWeight}%). El resto hasta 100% es la chance de "sin premio"; con 100% la máquina paga siempre.`;
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const err = validate();
    if (err) {
      setFormError(err);
      return;
    }
    setFormError(null);
    await createMutation.mutateAsync({
      type: 'slot',
      name: name.trim(),
      depositMin: depositMin || undefined,
      durationMin: durationMin ? Number(durationMin) : undefined,
      config: buildConfig(),
    });
    // Reset suave (deja símbolos default por comodidad).
    setName('');
    setDepositMin('');
    setDurationMin('');
    setMaxPlays('1');
    setPrizes(DEFAULT_PRIZES);
    onCreated?.();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="flex items-center gap-2 text-lg font-semibold text-primary">
        <Dice5 className="h-5 w-5" />
        Crear Tragamonedas
      </div>

      {/* Nombre */}
      <div className="space-y-1.5">
        <label className="text-sm font-medium">
          Nombre <span className="text-red-500">*</span>
        </label>
        <Input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="ej: Slots Friday"
        />
      </div>

      {/* Símbolos del carrete */}
      <div className="space-y-3">
        <div className="text-sm font-medium text-muted-foreground">
          Símbolos del carrete (mín 3)
        </div>
        <div className="grid gap-2 sm:grid-cols-3">
          {symbols.map((s) => (
            <div
              key={s.id}
              className="flex items-center gap-1.5 rounded-md border border-input bg-background p-1.5"
            >
              <Input
                value={s.emoji}
                onChange={(e) => updateSymbol(s.id, { emoji: e.target.value })}
                className="w-12 text-center text-lg"
                maxLength={4}
                placeholder="🍒"
              />
              <Input
                value={s.name}
                onChange={(e) => updateSymbol(s.id, { name: e.target.value })}
                className="flex-1"
                placeholder="Nombre"
              />
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="text-red-500 shrink-0"
                onClick={() => removeSymbol(s.id)}
                disabled={symbols.length <= 3}
                title="Eliminar símbolo"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            className="h-auto min-h-[52px] border-dashed"
            onClick={addSymbol}
          >
            <Plus className="mr-1 h-4 w-4" />
            Símbolo
          </Button>
        </div>
      </div>

      {/* Premios */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-muted-foreground">
            Premios (probabilidad total = 100%)
          </div>
          <span
            className={`text-xs font-mono ${
              totalWeight >= 100 ? 'text-red-500' : 'text-muted-foreground'
            }`}
          >
            {totalWeight}% configurado{' '}
            {totalWeight >= 100 ? '(debe ser <100%)' : `· ${100 - totalWeight}% sin premio`}
            {expectedFichasPayout > 0
              ? ` · payout esperado ≈ ${Math.round(expectedFichasPayout).toLocaleString('es-AR')} fichas/jugada`
              : ''}
          </span>
        </div>

        <div className="space-y-3">
          {prizes.map((p) => (
            <div
              key={p.id}
              className="rounded-lg border border-input bg-muted/30 p-3 space-y-2"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  value={p.emoji}
                  onChange={(e) => updatePrize(p.id, { emoji: e.target.value })}
                  className="w-12 text-center text-lg"
                  maxLength={4}
                  placeholder="💎"
                />
                <Input
                  value={p.label}
                  onChange={(e) => updatePrize(p.id, { label: e.target.value })}
                  className="flex-1 min-w-[120px]"
                  placeholder="JACKPOT"
                />
                <select
                  value={p.prizeKind}
                  onChange={(e) =>
                    updatePrize(p.id, { prizeKind: e.target.value as PrizeKind })
                  }
                  className="h-10 rounded-md border border-input bg-background px-2 text-sm"
                >
                  {PRIZE_KIND_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-1">
                  <span className="text-sm text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min="0"
                    value={p.amount}
                    onChange={(e) => updatePrize(p.id, { amount: e.target.value })}
                    className="w-24"
                    placeholder="50000"
                  />
                </div>
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={p.weight}
                    onChange={(e) => updatePrize(p.id, { weight: e.target.value })}
                    className="w-16"
                    placeholder="2"
                  />
                  <span className="text-sm text-muted-foreground">%</span>
                </div>
                <Button
                  type="button"
                  size="icon"
                  variant="ghost"
                  className="text-red-500 shrink-0"
                  onClick={() => removePrize(p.id)}
                  title="Eliminar premio"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>

              {/* Combo: 3 selects de símbolos */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Combo:</span>
                {p.combo.map((c, idx) => (
                  <select
                    key={idx}
                    value={c}
                    onChange={(e) => updatePrizeCombo(p.id, idx, e.target.value)}
                    className="h-9 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {symbolOptions.map((s) => (
                      <option key={s.id} value={s.emoji}>
                        {s.emoji} {s.name}
                      </option>
                    ))}
                  </select>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Button type="button" variant="outline" onClick={addPrize}>
          <Plus className="mr-1 h-4 w-4" />
          Agregar premio
        </Button>
      </div>

      {/* Depósito mínimo + duración + wager */}
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Depósito mínimo</label>
          <Input
            type="number"
            min="0"
            value={depositMin}
            onChange={(e) => setDepositMin(e.target.value)}
            placeholder="0"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Duración (min)</label>
          <Input
            type="number"
            min="0"
            value={durationMin}
            onChange={(e) => setDurationMin(e.target.value)}
            placeholder="60"
          />
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Tiradas por persona</label>
          <Input
            type="number"
            min="0"
            value={maxPlays}
            onChange={(e) => setMaxPlays(e.target.value)}
            placeholder="1"
          />
          <p className="text-[11px] text-muted-foreground">0 = sin límite</p>
        </div>
        <div className="space-y-1.5">
          <label className="text-sm font-medium">Wager x (bonos)</label>
          <Input
            type="number"
            min="0"
            step="0.1"
            value={wagerMultiplier}
            onChange={(e) => setWagerMultiplier(e.target.value)}
            placeholder="1"
          />
        </div>
      </div>

      {formError && (
        <div className="rounded-md bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {formError}
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={createMutation.isPending}>
          {createMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Crear tragamonedas
        </Button>
      </div>
    </form>
  );
}

export default SlotForm;
